import parse from "html-react-parser";

//  check if any editable element is focused
export const isAnyEditableElementFocused = () => {
  const el = document.activeElement;
  if (!el) return false;

  if (el.isContentEditable) return true;

  const tag = el.tagName.toLowerCase();
  if (tag === "textarea") return true;
  if (tag === "select") return true;

  if (tag === "input") {
    const type = (el.type || "text").toLowerCase();
    const nonTextTypes = new Set([
      "button",
      "checkbox",
      "color",
      "file",
      "hidden",
      "image",
      "radio",
      "range",
      "reset",
      "submit",
    ]);
    return !nonTextTypes.has(type);
  }

  // Slate editor: has data-slate-editor="true"
  if (el.hasAttribute("data-slate-editor")) return true;

  // Quill editor: has class .ql-editor
  if (el.classList.contains("ql-editor")) return true;

  return false;
};

export const isTableTargetedForDeletion = (element) => {
  if (!element || element.ElementType !== "Table" || element.IsElementLocked === true) {
    return false;
  }
  if (element.isElementSelected === true || element.IsWholeTableSelected === true) {
    return true;
  }
  return (element.TableDetails || []).some((row) =>
    (row?.RowCells || []).some((cell) => cell?.IsCellActive === true),
  );
};

/** Clear cell/whole-table selection without touching isElementSelected. */
export const clearTableCellSelectionState = (element) => {
  if (!element || element.ElementType !== "Table") {
    return element;
  }
  return {
    ...element,
    IsWholeTableSelected: false,
    SelectedColumn: { Column: null, IsSelected: false },
    TableDetails: (element.TableDetails || []).map((row) => ({
      ...row,
      IsSelectedRow: false,
      RowCells: (row.RowCells || []).map((cell) => ({
        ...cell,
        IsCellActive: false,
      })),
    })),
  };
};

/** Deselect an element and clear any lingering table cell targeting. */
export const deselectElement = (element) => ({
  ...clearTableCellSelectionState(element),
  isElementSelected: false,
});

/** Deselect every element on every page. */
export const deselectAllElements = (pages = []) =>
  (pages || []).map((page) => ({ ...page, Elements: (page.Elements || []).map((el) => deselectElement(el)) }));

export const markTablesWithActiveCellsSelected = (elements = []) =>
  (elements || []).map((element) =>
    isTableTargetedForDeletion(element) ? { ...element, isElementSelected: true } : element,
  );

//check if there is any selected text in the document
export const isAnyTextSelected = () => {
  const selection = window.getSelection();

  if (!selection) return false;

  return selection.type === 'Range' && selection.toString().trim().length > 0;
};


// check if any native element is focused 
export const isNativeFormElementFocused = () => {
  const el = document.activeElement;
  if (!el) return false;

  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select';
};

/** Blur canvas text editors so copy/paste shortcuts apply to elements, not in-place text. */
export const blurCanvasEditableFocus = () => {
  const el = document.activeElement;
  if (!el || el === document.body) return;

  const isCanvasTextEditor =
    el.isContentEditable ||
    el.hasAttribute?.("data-slate-editor") ||
    el.classList?.contains("ql-editor") ||
    el.tagName?.toLowerCase() === "textarea";

  if (isCanvasTextEditor) {
    el.blur();
  }

  const selection = window.getSelection();
  if (selection?.type === "Range") {
    selection.removeAllRanges();
  }
};

const HTML_BLOCK_TAGS =
  "p|div|h[1-6]|ul|ol|li|dl|dt|dd|blockquote|pre|table|caption|thead|tbody|tfoot|tr|td|th|" +
  "section|article|header|footer|nav|aside|main|figure|figcaption|form|fieldset|address|details|summary|hr";
const BLOCK_TAG_START = new RegExp(`^<\\/?(?:${HTML_BLOCK_TAGS})\\b`, "i");
const LEADING_WS_BEFORE_BLOCK = new RegExp(`^\\s+(?=<\\/?(?:${HTML_BLOCK_TAGS})\\b)`, "i");
const TRAILING_WS_AFTER_CLOSING_BLOCK = new RegExp(`(<\\/(?:${HTML_BLOCK_TAGS})>)(\\s+)$`, "i");
const isBlockTag = (tag) => BLOCK_TAG_START.test(tag);
const isClosingTag = (tag) => tag.startsWith("</");

/**
 * Style that keeps spaces and line breaks the user typed, the same way the editor shows them.
 * Plain HTML collapses every run of spaces into one, which broke slides that use spaces for
 * columns or indents.
 */
export const PRESERVE_SPACES_STYLE = { whiteSpace: "pre-wrap", overflowWrap: "break-word" };

/** `PRESERVE_SPACES_STYLE`, or nothing when a view must keep its old rendering. */
export const getRichTextWhiteSpaceStyle = (preserveSpaces = true) => (preserveSpaces ? PRESERVE_SPACES_STYLE : {});

/**
 * With `white-space: pre-wrap`, newlines and indentation that only come from how the HTML was
 * formatted would show up as blank lines. This removes that, and only that:
 *  - whitespace between two block tags (`</p>\n<p>`, `<div>\n  <p>`, `</p>   <p>`), and
 *  - newline whitespace at the very start or end of the content.
 * Anything inside a paragraph is never touched: typed spaces, typed blank lines (`<p>\n\n<b>x</b></p>`),
 * soft line breaks, `<br>` and inline elements are all kept (`<br>` is not treated as a block tag).
 * Limit: formatting whitespace inside a block (`<p>\n  Hello\n</p>`) is also kept, because it cannot be
 * told apart from typed text. The editor never writes HTML like that, only hand-written or imported HTML.
 * Missing data (null/undefined) becomes an empty string.
 */
export const stripHtmlFormattingWhitespace = (html) =>
  String(html ?? "")
    .replace(/(<[^<>]+>)(\s+)(?=(<[^<>]+>))/g, (match, left, whitespace, right) => {
      const isEmptyBlockInside = !isClosingTag(left) && isClosingTag(right);
      return isBlockTag(left) && isBlockTag(right) && !isEmptyBlockInside ? left : match;
    })
    .replace(LEADING_WS_BEFORE_BLOCK, (whitespace) => (whitespace.includes("\n") ? "" : whitespace))
    .replace(TRAILING_WS_AFTER_CLOSING_BLOCK, (match, tag, whitespace) => (whitespace.includes("\n") ? tag : match));

/** Parse saved rich-text HTML for display. `preserveSpaces = false` keeps the old rendering. */
export const parseRichHtml = (html, preserveSpaces = true) =>
  parse(preserveSpaces ? stripHtmlFormattingWhitespace(html) : `${html}`);

export { findTextFitTarget, measureTextFitSize } from "../../../AppComponents/textFitUtils.js";
